/**
 * Natural Language Module
 * Exports all natural language processing components
 */

export * from './ast.js';
export * from './intent.js';
export * from './lexer.js';
export * from './parser.js';

import { detectIntent } from './intent.js';
import { tokenize } from './lexer.js';
import { parse } from './parser.js';

/**
 * Strip # comment lines and normalise input for natural language parsing.
 * Also removes modifier expressions like (date only).
 */
function stripComments(input: string): string {
  return input
    .split('\n')
    .filter((line) => !line.trimStart().startsWith('#'))
    .join('\n')
    .trim();
}

/**
 * Main API: Parse natural language to expression
 */
export function parseNaturalLanguage(input: string): string | null {
  const clean = stripComments(input);
  if (!clean) return null;

  const _modifiers = {
    dateOnly: /\(date only\)/i.test(clean),
    ignoreCase: /\(ignore case\)/i.test(clean),
  };

  const cleanInput = clean.replace(/\s*\([^)]+\)/g, '').trim();

  const tokens = tokenize(cleanInput);
  const result = parse(tokens, clean);
  return result.expression;
}

/**
 * Parse with full details including confidence and alternatives
 */
export function parseNaturalLanguageDetailed(input: string) {
  const clean = stripComments(input);
  const tokens = tokenize(clean || input);
  return parse(tokens, clean || input);
}

/**
 * Get intent information
 */
export function analyzeIntent(input: string) {
  const tokens = tokenize(input);
  return detectIntent(tokens, input);
}

/**
 * Pattern example for autocomplete/documentation
 */
export interface PatternExample {
  label: string;
  detail: string;
  apply: string;
  intent: string;
  category: string;
}

/**
 * Get all natural language pattern examples
 * Used for autocomplete, documentation, and editor support
 * Single source of truth for what patterns are supported
 */
export function getPatternExamples(): PatternExample[] {
  return [
    {
      label: '{{field}} is at least {{value}}',
      detail: 'Minimum value (>=)',
      apply: '{{field}} is at least ',
      intent: 'comparison',
      category: 'Comparison',
    },
    {
      label: '{{field}} is greater than {{value}}',
      detail: 'Strictly greater than (>)',
      apply: '{{field}} is greater than ',
      intent: 'comparison',
      category: 'Comparison',
    },
    {
      label: '{{field}} is less than {{value}}',
      detail: 'Strictly less than (<)',
      apply: '{{field}} is less than ',
      intent: 'comparison',
      category: 'Comparison',
    },
    {
      label: '{{field}} is at most {{value}}',
      detail: 'Maximum value (<=)',
      apply: '{{field}} is at most ',
      intent: 'comparison',
      category: 'Comparison',
    },
    {
      label: '{{field}} is more than {{value}}',
      detail: 'Greater than (>)',
      apply: '{{field}} is more than ',
      intent: 'comparison',
      category: 'Comparison',
    },

    {
      label: '{{field}} is present',
      detail: 'Field has a value (not null)',
      apply: '{{field}} is present.',
      intent: 'presence',
      category: 'Presence',
    },
    {
      label: '{{field}} is empty',
      detail: 'Field is null or empty',
      apply: '{{field}} is empty.',
      intent: 'presence',
      category: 'Presence',
    },
    {
      label: '{{field}} must be provided',
      detail: 'Field is required',
      apply: '{{field}} must be provided.',
      intent: 'presence',
      category: 'Presence',
    },
    {
      label: 'Required',
      detail: 'Mark as required',
      apply: 'Required.',
      intent: 'presence',
      category: 'Presence',
    },

    {
      label: '{{field}} is between {{min}} and {{max}}',
      detail: 'Value in range [min, max]',
      apply: '{{field}} is between ',
      intent: 'range',
      category: 'Range',
    },
    {
      label: '{{field}} is within {{min}} to {{max}}',
      detail: 'Value within bounds',
      apply: '{{field}} is within ',
      intent: 'range',
      category: 'Range',
    },
    {
      label: '{{field}} is outside {{min}} and {{max}}',
      detail: 'Value outside range',
      apply: '{{field}} is outside ',
      intent: 'range',
      category: 'Range',
    },

    {
      label: '{{field}} contains {{text}}',
      detail: 'String contains substring',
      apply: '{{field}} contains "',
      intent: 'text-match',
      category: 'Text',
    },
    {
      label: '{{field}} starts with {{text}}',
      detail: 'String starts with prefix',
      apply: '{{field}} starts with "',
      intent: 'text-match',
      category: 'Text',
    },
    {
      label: '{{field}} ends with {{text}}',
      detail: 'String ends with suffix',
      apply: '{{field}} ends with "',
      intent: 'text-match',
      category: 'Text',
    },
    {
      label: '{{field}} has text',
      detail: 'String is not empty',
      apply: '{{field}} has text.',
      intent: 'text-match',
      category: 'Text',
    },
    {
      label: '{{field}} matches {{text}}',
      detail: 'String equals (case-sensitive)',
      apply: '{{field}} matches "',
      intent: 'text-match',
      category: 'Text',
    },

    {
      label: '{{field}} is checked',
      detail: 'Checkbox is true',
      apply: '{{field}} is checked.',
      intent: 'boolean',
      category: 'Boolean',
    },
    {
      label: '{{field}} is unchecked',
      detail: 'Checkbox is false or null',
      apply: '{{field}} is unchecked.',
      intent: 'boolean',
      category: 'Boolean',
    },

    {
      label: '{{date1}} is after {{date2}}',
      detail: 'First date > second date',
      apply: '{{date1}} is after ',
      intent: 'date-comparison',
      category: 'Date Comparison',
    },
    {
      label: '{{date1}} is before {{date2}}',
      detail: 'First date < second date',
      apply: '{{date1}} is before ',
      intent: 'date-comparison',
      category: 'Date Comparison',
    },
    {
      label: '{{date1}} is on or after {{date2}}',
      detail: 'First date >= second date',
      apply: '{{date1}} is on or after ',
      intent: 'date-comparison',
      category: 'Date Comparison',
    },
    {
      label: '{{date1}} is on or before {{date2}}',
      detail: 'First date <= second date',
      apply: '{{date1}} is on or before ',
      intent: 'date-comparison',
      category: 'Date Comparison',
    },
    {
      label: '{{date1}} is same or after {{date2}}',
      detail: 'Date >= (alternative syntax)',
      apply: '{{date1}} is same or after ',
      intent: 'date-comparison',
      category: 'Date Comparison',
    },

    {
      label: '{{date1}} at least {{N}} days after {{date2}}',
      detail: 'Date difference >= N days',
      apply: '{{date1}} at least ',
      intent: 'date-arithmetic',
      category: 'Date Arithmetic',
    },
    {
      label: '{{date1}} within {{N}} hours of {{date2}}',
      detail: 'Times within N hours',
      apply: '{{date1}} within ',
      intent: 'date-arithmetic',
      category: 'Date Arithmetic',
    },
    {
      label: '{{date1}} exactly {{N}} days after {{date2}}',
      detail: 'Exact date difference',
      apply: '{{date1}} exactly ',
      intent: 'date-arithmetic',
      category: 'Date Arithmetic',
    },
    {
      label: '{{field}} not more than {{N}} days ago',
      detail: 'Recent date check (relative to now)',
      apply: '{{field}} not more than ',
      intent: 'date-arithmetic',
      category: 'Date Arithmetic',
    },
    {
      label: '{{field}} at least {{N}} days ago',
      detail: 'Historical date check',
      apply: '{{field}} at least ',
      intent: 'date-arithmetic',
      category: 'Date Arithmetic',
    },

    {
      label: '{{field1}} equals {{field2}}',
      detail: 'Two fields are equal',
      apply: '{{field1}} equals ',
      intent: 'field-equality',
      category: 'Field Comparison',
    },
    {
      label: '{{field1}} same as {{field2}}',
      detail: 'Fields have same value',
      apply: '{{field1}} same as ',
      intent: 'field-equality',
      category: 'Field Comparison',
    },
    {
      label: '{{field1}} different from {{field2}}',
      detail: 'Fields are not equal',
      apply: '{{field1}} different from ',
      intent: 'field-equality',
      category: 'Field Comparison',
    },

    {
      label: '{{field}} required unless {{condition}}',
      detail: 'Conditional requirement',
      apply: '{{field}} required unless ',
      intent: 'conditional',
      category: 'Conditional',
    },

    {
      label: '{{condition1}} and {{condition2}}',
      detail: 'Both conditions must be true',
      apply: '',
      intent: 'combined',
      category: 'Combined',
    },
    {
      label: '{{condition1}} or {{condition2}}',
      detail: 'Either condition must be true',
      apply: '',
      intent: 'combined',
      category: 'Combined',
    },
  ];
}
